import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  FileStack,
  HelpCircle,
  UploadCloud,
  Wrench,
} from 'lucide-react'
import ProgressIndicator from '@/components/serge/ProgressIndicator'
import { getSergeProjectContext } from '@/services/sergeApi'
import QuoteFromOfferButton from '@/features/contracts/components/QuoteFromOfferButton.jsx'

interface StepStatus {
  status: string
  isCurrent: boolean
  isAccessible: boolean
}

type ProjectStep = 'import' | 'analysis' | 'clarification' | 'risks' | 'export'

const steps: Array<{
  label: string
  path: string
  apiStep: ProjectStep
  icon: typeof UploadCloud
}> = [
  { label: 'Import', path: 'upload', apiStep: 'import', icon: UploadCloud },
  { label: 'Analyse', path: 'analyse', apiStep: 'analysis', icon: Wrench },
  { label: 'Clarification', path: 'clarification', apiStep: 'clarification', icon: HelpCircle },
  { label: 'Risques', path: 'risks', apiStep: 'risks', icon: AlertTriangle },
  { label: 'Export', path: 'export', apiStep: 'export', icon: FileStack },
]

export default function SergeWorkspaceLayout() {
  const { projectId } = useParams()
  const location = useLocation()
  const [projectName, setProjectName] = useState('Projet')
  const [stepsStatus, setStepsStatus] = useState<Record<ProjectStep, StepStatus> | null>(null)
  const pathIndex = steps.findIndex((step) => location.pathname.includes(step.path))
  const currentIndex = useMemo(() => {
    if (stepsStatus) {
      const statusIndex = steps.findIndex(
        (step) => stepsStatus[step.apiStep]?.isCurrent
      )
      if (statusIndex >= 0) {
        return statusIndex
      }
    }
    return Math.max(0, pathIndex)
  }, [pathIndex, stepsStatus])

  useEffect(() => {
    if (!projectId) return
    const loadSteps = async () => {
      try {
        const context = await getSergeProjectContext(Number(projectId))
        setProjectName(context.projectName || `Projet ${projectId}`)
        setStepsStatus(context.stepsStatus || null)
      } catch {
        setProjectName(`Projet ${projectId}`)
      }
    }
    loadSteps()
  }, [projectId, location.pathname])

  const stepAccess = useMemo(() => {
    if (!stepsStatus) return new Map<string, any>()
    return new Map(steps.map((step) => [step.path, stepsStatus[step.apiStep]]))
  }, [stepsStatus])

  return (
    <div className="min-h-screen bg-bg">
      <div className="grid grid-cols-12 gap-0">
        <aside className="hidden lg:flex col-span-12 lg:col-span-2 border-r border-border bg-surface min-h-screen">
          <div className="w-full p-5 space-y-6">
            <Link
              to="/dashboard/projets"
              className="flex items-center gap-2 text-sm font-mono text-foreground-muted hover:text-primary transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au dashboard
            </Link>
            <Link
              to="/dashboard/legal-entities"
              className="flex items-center gap-2 text-sm font-mono text-foreground-muted hover:text-primary transition-colors border border-border bg-bg p-3 mb-4"
            >
              <Building2 className="w-4 h-4" />
              Entites legales
            </Link>
            <div>
              <p className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                Workspace
              </p>
              <p className="text-lg font-semibold text-foreground mt-2">{projectName}</p>
            </div>
            <nav className="space-y-3">
              {steps.map((step) => (
                <NavLink
                  key={step.path}
                  to={step.path}
                  onClick={(event) => {
                    const status = stepAccess.get(step.path)
                    if (status && !status.isAccessible) {
                      event.preventDefault()
                    }
                  }}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 border border-border bg-bg p-3 text-sm font-mono',
                      isActive
                        ? 'border-primary/60 text-primary'
                        : 'text-foreground hover:border-border-hover',
                      stepAccess.get(step.path)?.isAccessible === false &&
                        'opacity-40 cursor-not-allowed hover:border-border'
                    )
                  }
                >
                  <step.icon className="w-4 h-4" />
                  {step.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        <div className="col-span-12 lg:col-span-10">
          <header className="border-b border-border bg-surface px-6 py-6">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-xs font-mono text-foreground-muted uppercase tracking-[0.35em]">
                  Projet
                </p>
                <h1 className="text-2xl font-bold text-foreground mt-1">
                  {projectName}
                </h1>
                <p className="text-sm text-foreground-secondary mt-2">
                  Parcours complet du document a l export.
                </p>
              </div>
              {/* Offre -> devis : le devis reprend les spécifications de ce projet et son prospect */}
              <QuoteFromOfferButton projectId={projectId} />
            </div>
            <div className="mt-6">
              <ProgressIndicator steps={steps.map((step) => step.label)} currentIndex={currentIndex} />
            </div>
          </header>
          <main className="px-6 py-6">
            <Outlet context={{ 
              projectId: projectId ? Number(projectId) : null,
              projectName,
              stepsStatus,
              refreshProject: async () => {
                if (!projectId) return
                try {
                  const context = await getSergeProjectContext(Number(projectId))
                  setProjectName(context.projectName || `Projet ${projectId}`)
                  setStepsStatus(context.stepsStatus || null)
                } catch {}
              }
            }} />
          </main>
        </div>
      </div>
    </div>
  )
}
